import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import { FaRegArrowAltCircleUp } from "react-icons/fa";
import { isWeekend } from "../../../helpers/DateTransform";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { formatMoneyWithCurrency } from "./ReportsHelper";
import InfoDescriptionPopover from "./InfoDescriptionPopover";
import FadeInWrapper from "./FadeInWrapper";
import PopoverForTrendDescription from "./PopoverForTrendDescription";

Highcharts.setOptions({
  lang: {
    decimalPoint: ".",
    thousandsSep: ",",
  },
  credits: {
    enabled: false,
  },
});

const CHART_TITLE = {
  REV: "Revenue",
  MANAGED_SCORE: "Request Managing Score",
  REQ: "Request Funnel",
  LIFT: "Performance Lift",
  RRPM: "Managed Request RPM",
};

function _hideAllTooltips(trendCharts) {
  trendCharts.forEach((chart) => {
    for (let i = 0; i < chart.series.length; i++) {
      let points = chart.series[i].points;
      for (let j = 0; j < points.length; j++) {
        try {
          points[j].onMouseOut();
        } catch (err) {
          //console.log(err);
        }
      }
    }

    chart.tooltip.hide();
    chart.xAxis[0].hideCrosshair();
  });
}

function _initChartEventListeners(trendCharts) {
  let divIds = _.map(CHART_TITLE, (title) => {
    return `chart-id-${title}`;
  });

  // console.log(Highcharts.charts);

  let lastPointIndex = undefined;
  for (let i = 0; i < divIds.length; ++i) {
    (function (i) {
      let divId = divIds[i];
      let divEle = document.getElementById(divId);
      if (!divEle) return;
      let chartRectEle = divEle.getElementsByClassName(
        "highcharts-plot-background"
      )[0];

      divEle.addEventListener(
        "mousemove",
        _.debounce(function (e) {
          let box = chartRectEle.getBoundingClientRect();
          let x = e.clientX;
          let y = e.clientY;
          if (
            box.left >= x ||
            box.right <= x ||
            box.top >= y ||
            box.bottom <= y
          ) {
            if (lastPointIndex) {
              _hideAllTooltips(trendCharts);
            }
            lastPointIndex = undefined;
            return;
          }

          let chart = trendCharts[i];
          let event = chart.pointer.normalize(e);
          let point = chart.series[0].searchPoint(event, true);
          if (point) {
            let pointIndex = point.index;
            if (pointIndex === lastPointIndex) return;

            for (let j = 0; j < divIds.length; ++j) {
              if (trendCharts[(i + j + 1) % divIds.length]) {
                // console.log(
                //   trendCharts[(i + j + 1) % divIds.length],
                //   (i + j + 1) % divIds.length
                // );
                trendCharts[(i + j + 1) % divIds.length].series[0].points[
                  pointIndex
                ].highlight(e);
              }
            }
            lastPointIndex = pointIndex;
          }
        }, 0)
      );
    })(i);
  }
}

function _createAllChartOptions({ reports, selectedCurrency }) {
  const revChartOptions = _createRevChartOptions({
    reports,
    selectedCurrency,
  });
  const rrpmChartOptions = _createRrpmChartOptions({
    reports,
    selectedCurrency,
  });
  const reqChartOptions = _createReqChartOptions({
    reports,
    selectedCurrency,
  });
  const msChartOptions = _createMsChartOptions({
    reports,
    selectedCurrency,
  });
  const liftChartOptions = _createLiftChartOptions({
    reports,
    selectedCurrency,
  });

  return {
    revChartOptions,
    rrpmChartOptions,
    reqChartOptions,
    msChartOptions,
    liftChartOptions,
  };
}

function _filterCharts(charts) {
  const chartTitles = _.values(CHART_TITLE);
  return _.filter(charts, (c) => {
    if (!c) {
      return false;
    }

    const title = _.get(c, "title.textStr");
    return _.indexOf(chartTitles, title) !== -1;
  });
}

function _calculateTotalRevData({ reports }) {
  const totalIncreased = _.sumBy(reports, "increasedRevenue");
  const originalRevenue = _.sumBy(reports, "newOriginalRevenue");
  const notOnboardedRevenue = _.sumBy(reports, "notOnboardedRevenue");
  const additionalCost = _.sumBy(reports, "cost");

  const totalRevenue = totalIncreased + originalRevenue + notOnboardedRevenue;
  // const netIncreasedRevenue = totalIncreased - additionalCost;
  // const perceivedLift = _.round(
  //   originalRevenue > 0 ? (totalIncreased / originalRevenue) * 100 : 0,
  //   2
  // );

  return {
    totalRevenue,
    originalRevenue: originalRevenue + notOnboardedRevenue,
    additionalCost,
  };
}

class TrendCombinedChart extends React.Component {
  constructor(props) {
    super(props);

    const { reports, selectedCurrency, summaryData } = props;

    const {
      revChartOptions,
      rrpmChartOptions,
      reqChartOptions,
      msChartOptions,
      liftChartOptions,
    } = _createAllChartOptions({ reports, selectedCurrency });

    const { netIncreasedRevenue, perceivedLift } = summaryData;
    const { totalRevenue, originalRevenue, additionalCost } =
      _calculateTotalRevData({
        reports,
      });

    this.state = {
      revChartOptions,
      rrpmChartOptions,
      reqChartOptions,
      msChartOptions,
      liftChartOptions,

      totalRevenue,
      originalRevenue,
      additionalCost,
      netIncreasedRevenue,
      perceivedLift,
    };
  }

  componentDidMount() {
    const trendCharts = _filterCharts(Highcharts.charts);
    _initChartEventListeners(trendCharts);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.startDate !== this.props.startDate ||
      prevProps.endDate !== this.props.endDate ||
      prevProps.selectedPubId !== this.props.selectedPubId ||
      prevProps.selectedNetworkId !== this.props.selectedNetworkId ||
      prevProps.selectedYieldSetId !== this.props.selectedYieldSetId ||
      prevProps.selectedCurrency !== this.props.selectedCurrency
    ) {
      const { reports, selectedCurrency } = this.props;
      const {
        revChartOptions,
        rrpmChartOptions,
        reqChartOptions,
        msChartOptions,
        liftChartOptions,
      } = _createAllChartOptions({ reports, selectedCurrency });

      const { netIncreasedRevenue, perceivedLift } = this.props.summaryData;
      const { totalRevenue, originalRevenue, additionalCost } =
        _calculateTotalRevData({
          reports,
        });

      this.setState({
        revChartOptions,
        rrpmChartOptions,
        reqChartOptions,
        msChartOptions,
        liftChartOptions,
        totalRevenue,
        originalRevenue,
        netIncreasedRevenue,
        additionalCost,
        perceivedLift,
      });
    }
  }

  render() {
    const {
      revChartOptions,
      rrpmChartOptions,
      reqChartOptions,
      msChartOptions,
      liftChartOptions,
      totalRevenue,
      originalRevenue,
      additionalCost,
      netIncreasedRevenue,
      perceivedLift,
    } = this.state;
    const { selectedCurrency } = this.props;

    const WrapDataThing = ({ isEnd }) => {
      // const defaultClass = "text-3xl text-gray-400 font-light";
      return "";
      // <span className={`${defaultClass} ${isEnd ? "pl-2" : "pr-2"}`}>
      //   {isEnd ? ")" : "("}
      // </span>
    };

    const MathThing = ({ mathSign }) => {
      return (
        <div className="text-3xl px-2 pt-4 text-gray-400 font-light self-center">
          {mathSign}
        </div>
      );
    };

    const RevDataBlock = () => {
      return (
        <div className="bg-gray-100 flex items-end justify-between my-1 rounded">
          <div className="px-4 py-2">
            <div className="text-gray-600 text-xs font-medium">
              Total Revenue <b>with</b> Yieldbooster
            </div>
            <div className="flex items-center justify-center">
              <FadeInWrapper key={totalRevenue}>
                <WrapDataThing></WrapDataThing>
                <div className="pt-1">
                  {formatMoneyWithCurrency({
                    currency: selectedCurrency,
                    value: totalRevenue,
                  })}
                </div>
                <WrapDataThing isEnd={true}></WrapDataThing>
              </FadeInWrapper>
            </div>
          </div>

          <MathThing mathSign="-"></MathThing>

          <div className="px-4 py-2">
            <div className="text-gray-600 text-xs font-medium">
              Total Revenue <b>without</b> Yieldbooster
            </div>

            <div className="flex items-center justify-center">
              <FadeInWrapper key={originalRevenue}>
                <WrapDataThing></WrapDataThing>
                <div className="pt-1">
                  {formatMoneyWithCurrency({
                    currency: selectedCurrency,
                    value: originalRevenue,
                  })}
                </div>
                <WrapDataThing isEnd={true}></WrapDataThing>
              </FadeInWrapper>
            </div>
          </div>

          <MathThing mathSign="-"></MathThing>

          <div className="px-4 py-2">
            <div className="text-gray-600 text-xs font-medium">
              Extra Ad Serving Cost
            </div>
            <div className="flex items-center justify-center">
              <FadeInWrapper key={additionalCost}>
                <WrapDataThing></WrapDataThing>
                <div className="pt-1">
                  {formatMoneyWithCurrency({
                    currency: selectedCurrency,
                    value: additionalCost,
                  })}
                </div>
                <WrapDataThing isEnd={true}></WrapDataThing>
              </FadeInWrapper>
            </div>
          </div>

          <MathThing mathSign="="></MathThing>

          <div className="px-4 py-2">
            <div className="text-gray-600 text-xs font-medium">
              Net Increased Revenue (Perceived Lift)
            </div>
            <div className="flex items-center justify-center">
              <FadeInWrapper key={netIncreasedRevenue}>
                <div className="flex items-center">
                  <WrapDataThing></WrapDataThing>
                  <div className="pt-1">
                    {formatMoneyWithCurrency({
                      currency: selectedCurrency,
                      value: netIncreasedRevenue,
                    })}
                  </div>
                  <WrapDataThing isEnd={true}></WrapDataThing>
                </div>

                <div className="pl-2 text-sm">
                  <div
                    className={`font-bold flex items-center pt-1 ${
                      perceivedLift >= 0 ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {perceivedLift >= 0 ? (
                      <div className="pr-1 font-bold">
                        <FaRegArrowAltCircleUp></FaRegArrowAltCircleUp>
                      </div>
                    ) : (
                      ""
                    )}
                    {perceivedLift >= 0 ? "+" : ""}
                    {perceivedLift}%
                  </div>
                </div>
              </FadeInWrapper>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div id="container">
        <div
          id={`chart-id-${CHART_TITLE.REV}`}
          className="p-4 border rounded mb-4"
        >
          <div
            className="font-semibold text-lg text-gray-800"
            style={{ marginLeft: "54px", marginRight: "76px" }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {CHART_TITLE.REV}
                <PopoverForTrendDescription metricKey="TREND_GOOGLE_REVENUE"></PopoverForTrendDescription>
                {/* <InfoDescriptionPopover descKey="TREND_GOOGLE_REV"></InfoDescriptionPopover> */}
              </div>
              <div className="text-xs font-normal text-gray-600">
                * Current day's data is incomplete
              </div>
            </div>
            <RevDataBlock></RevDataBlock>
          </div>

          <HighchartsReact highcharts={Highcharts} options={revChartOptions} />
        </div>

        <div className="flex">
          <div className="w-1/2 mr-2">
            <div className="border rounded">
              <div
                id={`chart-id-${CHART_TITLE.MANAGED_SCORE}`}
                className="p-2 border-b"
              >
                <div
                  className="font-semibold text-lg text-gray-800 pt-2"
                  style={{ marginLeft: "54px" }}
                >
                  <div className="flex items-center">
                    {CHART_TITLE.MANAGED_SCORE}
                    <PopoverForTrendDescription metricKey="TREND_REQUEST_MANAGING_SCORE"></PopoverForTrendDescription>

                    {/* <InfoDescriptionPopover descKey="MANAGED_SCORE"></InfoDescriptionPopover> */}
                  </div>
                </div>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={msChartOptions}
                />
              </div>
              <div id={`chart-id-${CHART_TITLE.REQ}`} className="p-2">
                <div
                  className="font-semibold text-lg text-gray-800 pt-2"
                  style={{ marginLeft: "54px" }}
                >
                  <div className="flex items-center">
                    {CHART_TITLE.REQ}
                    <PopoverForTrendDescription metricKey="TREND_REQUEST_FUNNEL"></PopoverForTrendDescription>
                    {/* <InfoDescriptionPopover descKey="TREND_REQ"></InfoDescriptionPopover> */}
                  </div>
                </div>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={reqChartOptions}
                />
              </div>
            </div>
          </div>

          <div className="w-1/2">
            <div className="border rounded ml-2">
              <div id={`chart-id-${CHART_TITLE.LIFT}`} className="p-2 border-b">
                <div
                  className="font-semibold text-lg text-gray-800 pt-2"
                  style={{ marginLeft: "54px" }}
                >
                  <div className="flex items-center">
                    {CHART_TITLE.LIFT}
                    <PopoverForTrendDescription metricKey="TREND_PERFORMANCE_LIFT"></PopoverForTrendDescription>
                    {/* <InfoDescriptionPopover descKey="TREND_LIFT"></InfoDescriptionPopover> */}
                  </div>
                </div>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={liftChartOptions}
                />
              </div>

              <div id={`chart-id-${CHART_TITLE.RRPM}`} className="p-2">
                <div
                  className="font-semibold text-lg text-gray-800 pt-2"
                  style={{ marginLeft: "54px" }}
                >
                  <div className="flex items-center">
                    {CHART_TITLE.RRPM}
                    <PopoverForTrendDescription metricKey="TREND_MANAGED_GOOGLE_RRPM"></PopoverForTrendDescription>

                    {/* <InfoDescriptionPopover descKey="TREND_RRPM"></InfoDescriptionPopover> */}
                  </div>
                </div>
                <HighchartsReact
                  highcharts={Highcharts}
                  options={rrpmChartOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function _createRevChartOptions({ reports, selectedCurrency }) {
  let options = {
    ..._getDefaultChartOptions(selectedCurrency),
  };

  // height of chart
  options.chart.height = 320;
  // to make sure all charts are aligned (with the same margin)
  options.chart.marginLeft = 64;

  options.title = {
    text: CHART_TITLE.REV,
    // title is important when identifing graphs to synch highlight
    style: {
      display: "none",
    },
  };

  const { seriesData } = _getRevTrendData(reports);

  options.series = seriesData;

  options.yAxis = [
    {
      startOnTick: false,
      title: {
        text: "",
      },
      // a thicker line across 0
      plotLines: [
        {
          color: "#C5C5C5",
          width: 1,
          value: 0,
          zIndex: 5,
        },
      ],
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
    },
    {
      startOnTick: false,
      title: {
        text: "Requests",
      },
      opposite: true,
      softMin: 0,
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
    },
  ];

  return options;
}

function _createMsChartOptions({ reports, selectedCurrency }) {
  let options = {
    ..._getDefaultChartOptions(selectedCurrency),
  };

  // height of chart
  options.chart.height = 250;

  // to make sure all charts are aligned (with the same margin)
  options.chart.marginLeft = 64;
  options.chart.marginRight = 24;

  options.title = {
    text: CHART_TITLE.MANAGED_SCORE,
    // title is important when identifing graphs to synch highlight
    style: {
      display: "none",
    },
  };

  const { seriesData } = _getMsTrendData(reports);

  options.series = seriesData;

  options.yAxis = [
    {
      title: {
        text: "",
      },
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
      min: 0,
      max: 100,
    },
  ];

  return options;
}

function _createLiftChartOptions({ reports, selectedCurrency }) {
  let options = {
    ..._getDefaultChartOptions(selectedCurrency),
  };

  options.title = {
    text: CHART_TITLE.LIFT,
    // title is important when identifing graphs to synch highlight
    style: {
      display: "none",
    },
  };

  // height of chart
  options.chart.height = 250;

  // to make sure all charts are aligned (with the same margin)
  options.chart.marginLeft = 64;
  options.chart.marginRight = 24;

  const { seriesData } = _getLiftTrendData(reports);
  options.series = seriesData;

  options.yAxis = [
    {
      title: {
        text: "",
      },
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
    },
  ];

  return options;
}

function _createRrpmChartOptions({ reports, selectedCurrency }) {
  let options = {
    ..._getDefaultChartOptions(selectedCurrency),
  };

  options.title = {
    text: CHART_TITLE.RRPM,
    // title is important when identifing graphs to synch highlight
    style: {
      display: "none",
    },
  };

  // height of chart
  options.chart.height = 250;

  // to make sure all charts are aligned (with the same margin)
  options.chart.marginLeft = 64;
  options.chart.marginRight = 24;

  const { seriesData } = _getRpmTrendData(reports);
  options.series = seriesData;

  options.yAxis = [
    {
      title: {
        text: "",
      },
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
    },
  ];

  return options;
}

function _createReqChartOptions({ reports, selectedCurrency }) {
  let options = {
    ..._getDefaultChartOptions(selectedCurrency),
  };

  // height of chart
  options.chart.height = 250;

  // to make sure all charts are aligned (with the same margin)
  options.chart.marginLeft = 64;
  options.chart.marginRight = 24;

  options.title = {
    text: CHART_TITLE.REQ,
    // title is important when identifing graphs to synch highlight
    style: {
      display: "none",
    },
  };

  const { seriesData, min: reqMin, max: reqMax } = _getReqTrendData(reports);

  options.series = seriesData;

  options.yAxis = [
    {
      title: {
        text: "",
      },
      lineWidth: 2,
      offset: 10,
      tickWidth: 1,
      gridLineWidth: 0,
    },
  ];

  return options;
}

function _getRevTrendData(reports) {
  let notonboardedData = [];
  let originalData = [];
  let increasedData = [];
  let additionalAdServingCostData = [];
  let requestData = [];

  // get min of revenue lift
  let min, newMin;

  _.forEach(reports, (r) => {
    const date = new Date(r.date).getTime();

    newMin = r.grossRevenueLift;
    min = min ? (newMin < min ? newMin : min) : newMin;

    increasedData.push({
      x: date,
      y: r.increasedRevenue,
      needCurrency: true,
    });
    originalData.push({
      x: date,
      y: r.newOriginalRevenue,
      needCurrency: true,
    });
    notonboardedData.push({
      x: date,
      y: r.notOnboardedRevenue,
      needCurrency: true,
    });
    additionalAdServingCostData.push({
      x: date,
      y: r.cost * -1,
      needCurrency: true,
      isAbsolute: true, // no negative sign in tooltip
    });
    requestData.push({
      x: date,
      y: r.totalRequests,
    });
  });

  const seriesData = [
    {
      type: "column",
      stacking: "normal",
      name: "Increased Revenue",
      data: increasedData,
      yAxis: 0,
      color: "#ff993f",
      borderWidth: 0,
      zIndex: 5,
    },
    {
      type: "column",
      stacking: "normal",
      name: "Original Revenue",
      data: originalData,
      yAxis: 0,
      color: "#2E5FB0",
      borderWidth: 0,
      zIndex: 5,
    },
    {
      type: "column",
      stacking: "normal",
      name: "Not-Onboarded Revenue",
      data: notonboardedData,
      yAxis: 0,
      borderWidth: 0,
      color: "#C6DCFF",
      zIndex: 5,
    },
    {
      type: "column",
      stacking: "normal",
      name: "Extra Ad Serving Cost",
      data: additionalAdServingCostData,
      yAxis: 0,
      borderWidth: 0,
      color: "#C5C5C5",
      zIndex: 5,
    },
    {
      name: "Total Requests",
      type: "line",
      data: requestData,
      yAxis: 1,
      color: "#434348",
      shadow: true,
      marker: {
        radius: 0,
      },
      zIndex: 7,

      // show data label of highest value
      // dataLabels: {
      //   enabled: true,
      //   formatter: function () {
      //     let dataMax = this.series.dataMax;
      //     if (dataMax > 0 && this.y === dataMax) {
      //       return `${this.y}%`;
      //     }
      //   },
      // },
    },
  ];

  return {
    seriesData,
  };
}

function _getMsTrendData(reports) {
  let ms = [];

  _.forEach(reports, (r) => {
    const date = new Date(r.date).getTime();

    ms.push({
      x: date,
      y: r.managedScore,
      isPercentage: true,
    });
  });

  const seriesData = [
    {
      name: "Managing Score",
      type: "line",
      data: ms,
      color: "#434348",
      shadow: true,
      marker: {
        enabled: false,
      },
    },
  ];

  return { seriesData };
}

function _getReqTrendData(reports) {
  let oReq = [];
  let rReq = [];
  let bReq = [];
  let managedScore = [];

  let min, max, newMin, newMax;

  _.forEach(reports, (r) => {
    const date = new Date(r.date).getTime();

    newMin = Math.min(
      r.onboardedRequests,
      r.remnentRequests,
      r.boostingRequests
    );
    newMax = Math.max(
      r.onboardedRequests,
      r.remnentRequests,
      r.boostingRequests
    );
    min = min ? (newMin < min ? newMin : min) : newMin;
    max = max ? (newMax > max ? newMax : max) : newMax;

    oReq.push({
      x: date,
      y: r.onboardedRequests,
    });
    rReq.push({
      x: date,
      y: r.remnentRequests,
    });
    bReq.push({
      x: date,
      y: r.boostingRequests,
    });
    managedScore.push({
      x: date,
      y: r.managedScore,
      isPercentage: true,
    });
  });

  const seriesData = [
    {
      name: "Onboarded Requests",
      type: "area",
      data: oReq,
      color: "#87c7f2",
      yAxis: 0,
      lineWidth: 0,
      marker: {
        enabled: false,
      },
    },
    {
      name: "Eligible Requests",
      type: "area",
      data: rReq,
      color: "#1D85FF", // blue
      yAxis: 0,
      lineWidth: 0,
      marker: {
        enabled: false,
      },
    },
    {
      name: "Managed Requests",
      type: "area",
      data: bReq,
      color: "#ff993f",
      yAxis: 0,
      lineWidth: 0,
      marker: {
        enabled: false,
      },
    },
  ];
  return { seriesData, min, max };
}

function _getLiftTrendData(reports) {
  let pLifts = [];
  let mLifts = [];

  _.forEach(reports, (r) => {
    const date = new Date(r.date).getTime();

    pLifts.push({
      x: date,
      y: r.perceivedLift,
      isPercentage: true,
    });
    mLifts.push({
      x: date,
      y: r.grossRevenueLift,
      isPercentage: true,
    });
  });

  const seriesData = [
    {
      name: "Managed Lift",
      type: "line",
      data: mLifts,
      color: "#ff993f", // orange
      marker: {
        enabled: false,
      },
    },
    {
      name: "Perceived Lift",
      type: "line",
      data: pLifts,
      color: "#e53e3e", // red
      marker: {
        enabled: false,
      },
    },
  ];

  return {
    seriesData,
  };
}

function _getRpmTrendData(reports) {
  let wYb = []; // managedRRPM
  let wWithoutYb = []; // BenchmarkRRPM
  let lifts = [];

  let min, max, newMin, newMax;

  _.forEach(reports, (r) => {
    const date = new Date(r.date).getTime();

    newMin = Math.min(r.rRpmWithYB, r.rRpmWithoutYB);
    newMax = Math.max(r.rRpmWithYB, r.rRpmWithoutYB);
    min = min ? (newMin < min ? newMin : min) : newMin;
    max = max ? (newMax > max ? newMax : max) : newMax;

    wYb.push({
      x: date,
      y: r.rRpmWithYB,
      needCurrency: true,
    });
    wWithoutYb.push({
      x: date,
      y: r.rRpmWithoutYB,
      needCurrency: true,
    });
    lifts.push({
      x: date,
      y: r.grossRevenueLift,
      isPercentage: true,
    });
  });

  const seriesData = [
    {
      name: "Managed Request RPM",
      type: "line",
      data: wYb,
      color: "#ff993f", // orange
      marker: {
        enabled: false,
      },
    },
    {
      name: "Benchmark Request RPM",
      type: "line",
      data: wWithoutYb,
      color: "#1D85FF", // blue
      marker: {
        enabled: false,
      },
    },
  ];

  return {
    seriesData,
    min: min,
    max: max,
  };
}

function _getDefaultChartOptions(selectedCurrency) {
  const options = {
    title: {
      text: "",
    },

    chart: {
      // backgroundColor: "rgba(0,0,0,0)", // transparent
    },

    tooltip: {
      // zoomType: "x",
      backgroundColor: "rgba(255,255,255,1)",
      shared: true,
      useHTML: true,
      formatter: function () {
        return _createTooltipBodyFormat(this, selectedCurrency);
      },
    },
  };

  options.xAxis = {
    ...getXAxisConfigDateTime(),
  };

  options.legend = {
    layout: "horizontal",
    align: "center",
    verticalAlign: "bottom",

    // show square instead of circle
    symbolHeight: 12,
    symbolWidth: 12,
    symbolRadius: 0,

    itemStyle: {
      fontSize: 14,
    },
  };

  return options;
}

const currentDate = moment.utc().startOf("day").valueOf();
const blackSquareUnicode = "\u25A0";
const horizontalBarUnicode = "\u2015";
function _createTooltipBodyFormat(tooltip, selectedCurrency) {
  const headFormat = `<div style="font-size:14px; font-weight: bold; text-align: center;">${moment
    .utc(tooltip.x)
    .format("YYYY-MM-DD ddd")}${tooltip.x === currentDate ? "*" : ""}</div>`;

  let revBody = "";
  for (let point of tooltip.points) {
    revBody += `
      <tr style="font-size: larger;">
        <td style="padding-top: 4px; font-weight: bold; text-align: right;padding-right: 4px;">
          <span style="color:${point.color}">${
      point.series.userOptions.type === "line"
        ? horizontalBarUnicode
        : blackSquareUnicode
    }</span> ${point.series.name}: 
        </td>
        <td style="padding-top: 4px; text-align: right;">
          <b>
            ${
              point.point.needCurrency
                ? formatMoneyWithCurrency({
                    currency: selectedCurrency,
                    value: point.point.isAbsolute ? Math.abs(point.y) : point.y,
                  })
                : point.point.isPercentage
                ? `${Highcharts.numberFormat(point.y, 2)}%`
                : Highcharts.numberFormat(point.y, 0, ".", ",")
            }
          
          </b>
        </td>
      </tr>
    `;
  }

  const revSection = `
    <table style="font-size: 12px;">
      ${revBody}
    </table>
  `;

  return `${headFormat}${revSection}`;
}

function getXAxisConfigDateTime() {
  return {
    type: "datetime",
    labels: {
      formatter: function () {
        // console.log(this);
        const numOfTicks = this.axis.tickPositions.length;

        const m = moment.utc(this.value);
        const d = m.format("DD");
        // const d = m.format("MM/DD");
        const wd = `${numOfTicks > 12 ? "" : m.format("ddd")}`;

        let dateLabel = d;
        // highlight current day
        if (this.value === currentDate) {
          dateLabel = `<span style="font-weight: bold; color: #b21111;">${d}*</span>`;
        } else if (isWeekend(this.value)) {
          dateLabel = `<span style="font-weight: bold; color: black;">${d}</span>`;
        }

        let labelHtml = `${dateLabel} <br/> ${wd}`;

        if (d === "01" || this.isFirst) {
          return `${labelHtml} <br/> <span style="color: #3182ce;	letter-spacing: 0.05em;">${m
            .format("MMM")
            .toUpperCase()}</span>`;
        }

        return labelHtml;
      },
    },
    tickInterval: 24 * 60 * 60 * 1000, // 1 day
    crosshair: {
      color: "#5C5C61", // dark gray
    },
    gridLineWidth: 1,
  };
}

(function (H) {
  /**
   * Override the reset function, we don't need to hide the tooltips and crosshairs.
   */
  H.Pointer.prototype.reset = function () {
    return undefined;
  };

  /**
   * Highlight a point by showing tooltip, setting hover state and draw crosshair
   */
  H.Point.prototype.highlight = function (event) {
    // event = this.series.chart.pointer.normalize(event);
    try {
      this.onMouseOver(); // Show the hover marker
      //this.series.chart.tooltip.refresh(this); // Show the tooltip
      this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
    } catch (err) {
      // console.log(this.series.chart);
      console.log(err);
    }
  };

  /**
   * Synchronize zooming through the setExtremes event handler.
   */
  // H.syncExtremes = function (e) {
  //   let thisChart = this.chart;

  //   if (e.trigger !== "syncExtremes") {
  //     // Prevent feedback loop
  //     Highcharts.each(Highcharts.charts, function (chart) {
  //       if (chart && chart !== thisChart) {
  //         if (chart.xAxis[0].setExtremes) {
  //           // It is null while updating
  //           chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
  //             trigger: "syncExtremes",
  //           });
  //         }
  //       }
  //     });
  //   }
  // };
})(Highcharts);

export default TrendCombinedChart;
